import { useCallback, useMemo } from 'react';
import type { ParsedToken, PinnedOption } from '@gonfalon/dataviz-experimental/QueryBuilder';

import { selectedContextKindsFromTokens } from '../../../CreateMetricModal/MetricEventQueryBuilder/eventQueryConversion';

import { setContextKindInQuery } from './pinnedContextKindQuery';

type QueryDispatch = (action: { type: 'SET_QUERY'; query: string }) => void;

export function usePinnedContextKind({
  parsedQuery,
  dispatch,
  pinnedOptions,
  isEditable,
}: {
  parsedQuery: ParsedToken[];
  dispatch: QueryDispatch;
  pinnedOptions: PinnedOption[];
  isEditable: boolean;
}): {
  selectedKinds: string[];
  pinnedValue: string | undefined;
  effectivePinnedOptions: PinnedOption[] | undefined;
  onPinnedChange: (value: string | undefined) => void;
} {
  const selectedKinds = useMemo(() => selectedContextKindsFromTokens(parsedQuery), [parsedQuery]);
  const pinnedValue = selectedKinds[0];

  // Synthesize the current pin while kinds load so the dropdown stays visible.
  const effectivePinnedOptions = useMemo<PinnedOption[] | undefined>(() => {
    if (!isEditable && !pinnedValue) {
      return undefined;
    }
    if (pinnedValue && !pinnedOptions.some((option) => option.value === pinnedValue)) {
      return [{ value: pinnedValue, label: pinnedValue }, ...pinnedOptions];
    }
    if (pinnedOptions.length > 0) {
      return pinnedOptions;
    }
    return pinnedValue ? [{ value: pinnedValue, label: pinnedValue }] : undefined;
  }, [isEditable, pinnedOptions, pinnedValue]);

  const onPinnedChange = useCallback(
    (value: string | undefined) => {
      dispatch({ type: 'SET_QUERY', query: setContextKindInQuery(parsedQuery, value) });
    },
    [parsedQuery, dispatch],
  );

  return { selectedKinds, pinnedValue, effectivePinnedOptions, onPinnedChange };
}
